import React, { useEffect, useRef, useState } from "react";
import { Check, X, ScrollText } from "lucide-react";
import { PART1, PART2 } from "../lib/rules";

// Duas partes, dois aceites separados. Cada parte abre num leitor: o botão "Li e aceito" só liga depois de rolar o texto até o fim.
function Reader({ part, onClose, onAccept }) {
  const ref = useRef(null);
  const [end, setEnd] = useState(false);
  const check = () => {
    const el = ref.current; if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setEnd(true);
  };
  useEffect(() => { const el = ref.current; if (el && el.scrollHeight <= el.clientHeight + 24) setEnd(true); }, []);
  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose} data-testid="rules-reader">
      <div className="w-full max-w-[760px] max-h-[90vh] flex flex-col rounded-2xl border border-[var(--color-main)]/40 bg-[#0b0b10] shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 p-5 border-b border-white/10">
          <div>
            <h3 className="text-lg font-black text-white uppercase italic">{part.title}</h3>
            <p className="text-[11px] text-white/45 font-bold mt-1">Role até o fim para liberar o botão de aceite.</p>
          </div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white cursor-pointer"><X size={20} /></button>
        </div>
        <div ref={ref} onScroll={check} className="flex-1 overflow-y-auto p-5 space-y-5 text-sm text-white/80 leading-relaxed">
          {part.sections.map((s) => (
            <section key={s.id}>
              <h4 className="text-white font-black mb-2">{s.title}</h4>
              {s.body && s.body.map((p) => <p key={p} className="mb-2">{p}</p>)}
              {s.list && <ol className="list-decimal pl-5 space-y-2">{s.list.map((l) => <li key={l}>{l}</li>)}</ol>}
            </section>
          ))}
          <p className="text-[11px] text-white/35 pt-2">Fim do documento.</p>
        </div>
        <div className="p-4 border-t border-white/10 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <span className="text-[11px] text-white/45 font-bold">{end ? "Você chegou ao fim. Pode aceitar." : "Role o texto até o final…"}</span>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="px-5 py-3 rounded-xl bg-white/5 border border-white/10 text-white/70 text-xs font-black uppercase cursor-pointer">Fechar</button>
            <button type="button" disabled={!end} onClick={onAccept} data-testid="rules-accept-btn"
              className="px-5 py-3 rounded-xl bg-[var(--color-main)] disabled:opacity-35 text-white text-xs font-black uppercase tracking-wider cursor-pointer flex items-center gap-2"><Check size={14} /> Li e aceito</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RulesAcceptance({ value, onChange, compact = false }) {
  const [open, setOpen] = useState(null);   // "p1" | "p2" | null
  const parts = [PART1, PART2];
  const accept = (id) => { onChange({ ...value, [id]: true }); setOpen(null); };
  return (
    <div className="space-y-3" data-testid="rules-acceptance">
      {!compact && <label className="text-[10px] font-black text-white/40 uppercase tracking-[2px] ml-1">REGRAS (OBRIGATÓRIO)</label>}
      {parts.map((p) => {
        const ok = !!value[p.id];
        return (
          <button key={p.id} type="button" onClick={() => setOpen(p.id)} data-testid={`rules-${p.id}`}
            className={`w-full text-left flex items-center gap-3 rounded-xl border px-4 py-3 transition-all cursor-pointer ${ok ? "border-emerald-500/50 bg-emerald-500/10" : "border-[var(--color-main)]/50 bg-[var(--color-main)]/10 hover:bg-[var(--color-main)]/20"}`}>
            <span className={`w-9 h-9 shrink-0 rounded-lg flex items-center justify-center ${ok ? "bg-emerald-500 text-black" : "bg-[var(--color-main)] text-white"}`}>{ok ? <Check size={18} strokeWidth={3} /> : <ScrollText size={18} />}</span>
            <span className="min-w-0">
              <span className="block text-xs font-black text-white uppercase tracking-wider">{p.title}</span>
              <span className="block text-[11px] text-white/50 font-bold">{ok ? "Aceito" : "Clique para ler e aceitar"} · {p.short}</span>
            </span>
          </button>
        );
      })}
      {open && <Reader part={open === "p1" ? PART1 : PART2} onClose={() => setOpen(null)} onAccept={() => accept(open)} />}
    </div>
  );
}
